import { useEffect } from 'react';
import { loadAuth, useAuth } from '../sync/authStore';
import { startUploadQueue } from '../sync/uploadQueue';
import { Window } from '../ui/Window';
import { AdminPage } from './AdminPage';
import { BoardPage } from './BoardPage';
import { Dashboard } from './Dashboard';
import { LoginPage } from './LoginPage';
import { RegisterPage } from './RegisterPage';
import { SharePage } from './SharePage';
import { Workspace } from './Workspace';
import { useRoute } from './router';
import { UpdateNotice } from './UpdateNotice';
import { startUpdateChecks } from './version';

export function App() {
  const route = useRoute();
  const status = useAuth((s) => s.status);
  const setupNeeded = useAuth((s) => s.setupNeeded);
  const isAdmin = useAuth((s) => s.user?.isAdmin ?? false);

  useEffect(() => {
    void loadAuth();
    void startUploadQueue();
    startUpdateChecks();
  }, []);

  return (
    <>
      <Routes route={route} status={status} setupNeeded={setupNeeded} isAdmin={isAdmin} />
      <UpdateNotice />
    </>
  );
}

function Routes({
  route,
  status,
  setupNeeded,
  isAdmin,
}: {
  route: ReturnType<typeof useRoute>;
  status: string;
  setupNeeded: boolean;
  isAdmin: boolean;
}) {
  // Share links work with or without an account; the server grants access per session.
  if (route.name === 'share') return <SharePage key={route.token} token={route.token} view={route.view} />;

  // Local boards never talk to the server, so they work logged out and offline.
  if (route.name === 'local') return <BoardPage key={route.id} boardId={route.id} remote={false} view={route.view} />;

  if (status === 'loading') {
    return (
      <main className="desktop">
        <Window title="VDS">
          <p>Connecting…</p>
        </Window>
      </main>
    );
  }

  if (status === 'anon') {
    if (route.name === 'register' || setupNeeded) {
      return <RegisterPage invite={route.name === 'register' ? route.invite : null} setup={setupNeeded} />;
    }
    return <LoginPage />;
  }

  switch (route.name) {
    case 'board':
      return <Workspace primary={route.id} split={route.split} view={route.view} />;
    case 'admin':
      // members who type the URL just get their boards
      return isAdmin ? <AdminPage /> : <Dashboard />;
    default:
      return <Dashboard />;
  }
}
